"use client";

import { type FieldMetadata, getInputProps } from "@conform-to/react";
import Link from "next/link";
import type { ComponentProps } from "react";

import { FieldError } from "@/components/forms/form-error";
import { INLINE_LINK_CLASS } from "@/components/ui/inline-link";

// Per-document conspicuous-assent checkbox (Chabolla v. ClassPass /
// Berman v. Freedom Financial standard) — wrapping <label> gives
// screen readers the full "I have read and agree to..." string;
// embedded link opens the binding text in a new tab so form state
// survives the user reading it.
//
// Used by both the standalone /legal/accept flow (existing-user
// re-acceptance) and the onboarding form (initial bundled
// acceptance + onboarding data submit).

const DOCUMENTS = {
  // The Terms require users to be 18 or older, so accepting them attests to it.
  tos: {
    label: "Terms of Service",
    href: "/legal/terms",
    assent: "I am 18 or older and have",
  },
  privacy: {
    label: "Privacy Policy",
    href: "/legal/privacy",
    assent: "I have",
  },
  lifetime_terms: {
    label: "Lifetime & Guarantee Terms",
    href: "/legal/guarantee",
    assent: "I have",
  },
} as const;

export type LegalDocument = keyof typeof DOCUMENTS;

export function AssentCheckbox(props: {
  document: LegalDocument;
  version: string;
  input: ComponentProps<"input">;
  error?: { id?: string; message?: string };
}) {
  const doc = DOCUMENTS[props.document];
  return (
    <div className="flex flex-col gap-1">
      <label className="flex items-start gap-3 text-sm">
        <input type="checkbox" {...props.input} className="mt-0.5 size-4" />
        <span>
          {doc.assent} read and agree to the{" "}
          <Link
            href={doc.href}
            // In the native shell, Capacitor hands _blank targets to the
            // system browser — DELIBERATELY kept: an in-place navigation
            // was tried and reverted (review 2026-07-22) because leaving
            // the page discards the half-filled Conform form state on
            // no-store pages, punishing exactly the users who read the
            // terms. Safari-over-the-app preserves the form untouched.
            target="_blank"
            rel="noopener noreferrer"
            className={INLINE_LINK_CLASS}
          >
            {doc.label} (v{props.version})
          </Link>
          .
        </span>
      </label>
      <FieldError id={props.error?.id} message={props.error?.message} />
    </div>
  );
}

export function DocumentCheckbox(props: {
  field: FieldMetadata<unknown>;
  document: LegalDocument;
  version: string;
}) {
  const { key, ...rest } = getInputProps(props.field, { type: "checkbox" });
  return (
    <AssentCheckbox
      key={key}
      document={props.document}
      version={props.version}
      input={rest}
      error={{ id: props.field.errorId, message: props.field.errors?.[0] }}
    />
  );
}
